import { useState } from 'react'
import { MoreHorizontal, Pencil, Trash2, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { Project, ProjectStats } from '@/lib/api/client'
import {
  COMPANY_TYPE_DOT,
  COMPANY_TYPE_LABELS,
  COMPANY_TYPE_TINT,
  MARKET_LABELS,
  PRODUCT_TYPE_LABELS,
  formatRelativeTime,
} from './portfolioConstants'

interface ProjectCardProps {
  project: Project
  stats?: ProjectStats
  onOpen: () => void
  onEdit: () => void
  onDelete: () => void
}

const PHASE_LABELS: Record<string, string> = {
  explore: 'Explore',
  problem: 'Discover',
  discover: 'Discover',
  validate: 'Validate',
  solution: 'Validate',
  define: 'Define',
  market: 'Define',
  product: 'Build',
  build: 'Build',
  growth: 'Scale',
  scale: 'Scale',
}

function computeProgress(phaseProgress: number, stats?: ProjectStats): number {
  if (phaseProgress > 0) return phaseProgress
  if (!stats) return 0
  // Proxy: each intelligence type filled counts 25 points
  return (
    (stats.documents > 0 ? 25 : 0) +
    (stats.personas > 0 ? 25 : 0) +
    (stats.competitors > 0 ? 25 : 0) +
    (stats.interviews > 0 ? 25 : 0)
  )
}

function bylineText(project: Project): string {
  const parts: string[] = []
  if (project.market) parts.push(MARKET_LABELS[project.market])
  if (project.productType) parts.push(PRODUCT_TYPE_LABELS[project.productType])
  if (project.industry) parts.push(project.industry)
  return parts.join(' · ')
}

export function ProjectCard({ project, stats, onOpen, onEdit, onDelete }: ProjectCardProps) {
  const [expanded, setExpanded] = useState(false)
  const isAdmin = project.role === 'admin'

  const rawProgress = stats?.phaseProgress ?? 0
  const progress = computeProgress(rawProgress, stats)
  const phaseLabel = PHASE_LABELS[stats?.currentPhaseId ?? ''] ?? 'Not started'
  const byline = bylineText(project)
  const dotColor = project.companyType ? COMPANY_TYPE_DOT[project.companyType] : 'bg-warm-400'
  const stageTint = project.companyType ? COMPANY_TYPE_TINT[project.companyType] : 'text-text-muted'
  const stageLabel = project.companyType ? COMPANY_TYPE_LABELS[project.companyType] : null

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() }
      }}
      className="group relative flex flex-col gap-2 bg-surface border border-border-subtle rounded-xl p-3.5 cursor-pointer transition-all hover:border-border-strong hover:shadow-[0_1px_2px_rgba(13,23,28,0.04),0_4px_14px_rgba(13,23,28,0.04)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/30"
    >
      {/* Row 1: dot + name + stage + menu */}
      <div className="flex items-center gap-2 min-w-0">
        <span className={cn('shrink-0 w-2 h-2 rounded-full', dotColor)} />
        <h3 className="flex-1 font-display font-semibold text-[15px] text-text leading-tight tracking-tight truncate">
          {project.name}
        </h3>
        {stageLabel && (
          <span className={cn('shrink-0 text-[11px] font-medium uppercase tracking-wider', stageTint)}>
            {stageLabel}
          </span>
        )}
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit() }}>
                <Pencil className="mr-2 h-4 w-4" />Edit details
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-danger-fg focus:text-danger-fg"
                onClick={(e) => { e.stopPropagation(); onDelete() }}
              >
                <Trash2 className="mr-2 h-4 w-4" />Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Row 2: description */}
      <p className={cn(
        'text-[13px] leading-snug',
        project.description ? 'text-text-secondary line-clamp-2' : 'text-text-muted/70 italic text-[12px]'
      )}>
        {project.description || 'No description yet.'}
      </p>

      {/* Row 3: progress bar */}
      <div className="flex flex-col gap-1">
        <div className="h-1 bg-warm-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-accent-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-[11px] text-text-muted">
          <span className="font-medium text-text-secondary">{phaseLabel}</span>
          <span>{progress}%</span>
        </div>
      </div>

      {/* Expandable stats + byline */}
      {expanded && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-2 border-t border-border-subtle text-[12px] text-text-secondary">
          <Stat n={stats?.personas ?? 0} label="personas" />
          <Stat n={stats?.competitors ?? 0} label="competitors" />
          <Stat n={stats?.documents ?? 0} label="docs" />
          <Stat n={stats?.interviews ?? 0} label="interviews" />
          <span className="ml-auto text-[11px] text-text-muted">
            {formatRelativeTime(stats?.lastActivityAt ?? null)}
          </span>
          {byline && (
            <span className="w-full text-[11px] text-text-muted">{byline}</span>
          )}
        </div>
      )}

      {/* Expand toggle */}
      <button
        aria-label={expanded ? 'Hide details' : 'Show details'}
        onClick={(e) => { e.stopPropagation(); setExpanded(s => !s) }}
        className="absolute bottom-2 right-2 w-5 h-5 flex items-center justify-center text-text-muted/50 hover:text-text-muted transition-colors"
      >
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform duration-200', expanded && 'rotate-180')} />
      </button>
    </div>
  )
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="font-semibold text-text tabular-nums">{n}</span>
      <span className="text-text-muted">{label}</span>
    </span>
  )
}
